import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
  Chip,
  Grid,
  useTheme,
  Divider,
} from '@mui/material';
import {
  CloseRounded as CloseRoundedIcon,
  CurrencyExchangeRounded as CurrencyExchangeRoundedIcon,
  FlashOnRounded as FlashOnRoundedIcon,
  WidgetsRounded as WidgetsRoundedIcon,
  CalculateRounded as CalculateRoundedIcon,
  CloudOffRounded as CloudOffRoundedIcon,
  GitHub as GitHubIcon,
  InfoRounded as InfoRoundedIcon,
  VerifiedRounded as VerifiedRoundedIcon,
  ShieldRounded as ShieldRoundedIcon,
} from '@mui/icons-material';
import { triggerHaptic } from '../utils/nativeBridge';

const AboutModal = ({ open, onClose, t, language = 'fa' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const features = [
    {
      icon: <FlashOnRoundedIcon sx={{ color: '#EAB308' }} />,
      title: t('featLiveRates'),
      desc: t('featLiveRatesDesc'),
      bg: isDark ? 'rgba(234, 179, 8, 0.12)' : 'rgba(234, 179, 8, 0.08)',
    },
    {
      icon: <WidgetsRoundedIcon sx={{ color: '#3B82F6' }} />,
      title: t('featWidgets'),
      desc: t('featWidgetsDesc'),
      bg: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
    },
    {
      icon: <CalculateRoundedIcon sx={{ color: '#10B981' }} />,
      title: t('featConverter'),
      desc: t('featConverterDesc'),
      bg: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
    },
    {
      icon: <CloudOffRoundedIcon sx={{ color: '#8B5CF6' }} />,
      title: t('featOffline'),
      desc: t('featOfflineDesc'),
      bg: isDark ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.08)',
    },
  ];

  const handleClose = () => {
    triggerHaptic('light');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400,
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-start', sm: 'center' },
          pt: {
            xs: 'calc(env(safe-area-inset-top, 24px) + 20px)',
            sm: 3,
          },
          pb: {
            xs: 'calc(env(safe-area-inset-bottom, 16px) + 20px)',
            sm: 3,
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: isDark
            ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(11, 15, 25, 0.98) 100%)'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
          boxShadow: isDark
            ? '0 24px 48px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.06)'
            : '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
          m: { xs: 1.5, sm: 2 },
          maxHeight: {
            xs: 'calc(100dvh - env(safe-area-inset-top, 24px) - env(safe-area-inset-bottom, 16px) - 50px)',
            sm: '85vh',
          },
        },
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <InfoRoundedIcon color="primary" sx={{ fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t('aboutApp')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
        {/* Brand & Hero Banner */}
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3.5,
              background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
              boxShadow: '0 8px 24px rgba(37, 99, 235, 0.35)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              mb: 1.5,
            }}
          >
            <CurrencyExchangeRoundedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
              {t('appTitle')}
            </Typography>
            <Chip
              label={`${t('version')} ۱.۶`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 800, height: 22, fontSize: '0.725rem' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, maxWidth: 440, mx: 'auto', mt: 0.5, lineHeight: 1.6 }}>
            {t('aboutTagline')}
          </Typography>
        </Box>

        {/* Detailed App Description */}
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            mb: 3,
          }}
        >
          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.8, fontSize: '0.875rem' }}>
            {t('aboutDesc')}
          </Typography>
        </Box>

        {/* Features Highlights Grid */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          {t('features')}
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {features.map((item, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Box
                sx={{
                  p: 1.75,
                  height: '100%',
                  borderRadius: 2.5,
                  backgroundColor: item.bg,
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                }}
              >
                <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', fontSize: '0.75rem' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* Developer Info & Links */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
              {t('developer')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              {t('developedBy')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GitHubIcon />}
            href="https://github.com/Incognito-Coder/arzgo-pwa"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => triggerHaptic('light')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              py: 0.75,
              px: 1.5,
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            }}
          >
            {t('sourceCode')}
          </Button>
        </Box>

        {/* Disclaimer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.25,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.05)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
          }}
        >
          <ShieldRoundedIcon sx={{ fontSize: 18, color: '#EF4444', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: isDark ? '#FCA5A5' : '#B91C1C', fontSize: '0.725rem', lineHeight: 1.5 }}>
            <strong>{t('disclaimerTitle')}:</strong> {t('disclaimerText')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AboutModal;
